/**
 * SnapshotChange — select a snapshot on an external device (DEC-151).
 *
 * The sibling of PresetChange, and deliberately NOT a mode on it: a preset
 * and a snapshot are different concepts on the pedal, and the wire shapes
 * reflect that. A preset change is THREE ordered messages and a reload; a
 * snapshot change is ONE message and seamless. See `SnapshotChangeItem` in
 * projectStore.ts for the reasoning and the wire format.
 *
 * ## One message, and never a Program Change
 *
 * The one hard rule of this file: it must never send CC32 or a PC. Sending
 * either would reload the preset, which is exactly the audible gap snapshots
 * exist to avoid — and it is the specific defect that made this item
 * necessary in the first place.
 *
 * ## Reuses `.midi-button`, per DEC-121
 *
 * Same reasoning as PresetChange: the face is a button face, so reusing
 * FireButton's classes means this tile is skinned identically across all
 * seven skins with zero new CSS, and `skin_matches_workbench` keeps
 * meaning something rather than gaining a surface it does not know about.
 *
 * ## Dead state is loud, per DEC-138
 *
 * A pedal lives on its OWN MIDI port, not the rig's default (Ableton's).
 * port/channel resolve LIVE from the tile's bound `GuitarPedal` (Connected
 * Devices, DEC-152 amended 2026-08-04) — no pedal bound, or one that's been
 * removed, means "this tile cannot possibly work", and it says so on its
 * face instead of tapping silently into Ableton.
 */

import { createSignal, Show, type Accessor } from 'solid-js';
import { editOwnsGesture, selectedItemId, type SnapshotChangeItem, type SnapshotTarget, type LayoutColor } from '../layout/store.ts';
import { activePedals } from '../layout/projectStore.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { fireAction, type FireResult } from '../opbridge/dispatch.ts';
import { bridgeConnectedReactive } from '../opbridge/store.ts';
import { ulid } from '../utils/ulid.ts';
import { recordHelixCommand, helixLastCommanded } from '../helix/lastCommanded.ts';
import { pushToast } from '../ui/toast.ts';
import { IconUnlinked } from './PedalStatusIcon.tsx';
import type { MidiCcStep } from '@opbuttons/schema';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';
import { timelineLit } from '../timeline/canvasLights.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

/** Helix reserved CC — official 3.80 Owner's Manual. DEVICE-DEFINED, not
 *  rig-allocatable: never drawn from the project's rolling CC counter. */
const CC_SNAPSHOT = 69;

/** CC69 values above the eight absolute slots. Documented in the manual's
 *  MIDI table; they are the reason `snapshot` is a union rather than a
 *  plain 0-7 number. */
const CC_VALUE_NEXT = 8;
const CC_VALUE_PREV = 9;

interface SnapshotChangeProps {
  item: SnapshotChangeItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** The single CC69 value this item sends. Exported for the smoke, which
 *  asserts the mapping without driving the UI. */
export function snapshotCcValue(target: SnapshotTarget): number {
  if (target === 'next') return CC_VALUE_NEXT;
  if (target === 'prev') return CC_VALUE_PREV;
  return target & 0x7f;
}

/** Face text when the author has not named the tile.
 *
 *  ⚠️ The old reasoning here ("snapshot NAMES live only inside the preset on
 *  the pedal and are not readable, so the author types 'LEAD' themselves") is
 *  now only HALF true, corrected 2026-08-28. Unreadable over MIDI, yes - but
 *  OPBridge reads them over USB and has since 2026-08-12 (shipped to the mesh
 *  2026-08-27, OPBridge S209):
 *
 *    POST /admin/helix/preset-detail {setlist, preset}  -> 8 names, direct-
 *      addressed, no MIDI select, the pedal's active preset never changes
 *    POST /admin/helix/snapshots/sweep                  -> whole device, ~7min
 *    GET  /admin/helix/snapshots                        -> sweep progress
 *
 *  Each name comes with `custom_named`, distinguishing an operator-renamed
 *  snapshot from an untouched "SNAPSHOT N" default - which is exactly the
 *  signal needed to decide whether a real name beats `SNAP N`. Verified by
 *  OPBridge across all 1024 presets.
 *
 *  ⚠️ If that endpoint ever returns `snapshots: []`, treat it as "ask again",
 *  never as "this preset has no snapshots". A rare valid-but-empty device read
 *  used to produce a silent zero; OPBridge added a retry, and per their own
 *  finding the empty array was never a legitimate answer.
 *
 *  Until that is wired, the fallback stays the slot number. */
export function snapshotLabel(target: SnapshotTarget): string {
  if (target === 'next') return 'NEXT';
  if (target === 'prev') return 'PREV';
  return `SNAP ${target + 1}`;
}

export function SnapshotChange(props: SnapshotChangeProps) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [fault, setFault] = createSignal(false);

  const pedal = () => {
    const id = props.item.pedalId;
    return id ? activePedals().find((pd) => pd.id === id) : undefined;
  };
  const noPort = () => !pedal()?.port;
  /** Liveness tracks the SPECIFIC bridge this pedal is wired to (per-item
   *  bridge affinity), same reasoning as PresetChange. */
  const live = () => bridgeConnectedReactive(pedalBridgeRowId(pedal())) && !noPort();
  /** True specifically when the pedal is properly authored (bound + ported)
   *  but its bridge is offline, same reasoning + same wording as
   *  PresetChange's `isDisconnected`. */
  const isDisconnected = () => !!pedal() && !noPort() && !bridgeConnectedReactive(pedalBridgeRowId(pedal()));

  /** ONE CC. No setlist, no PC — see the file header. */
  async function fire(): Promise<void> {
    const pd = pedal();
    const port = pd?.port;
    if (!port) return;

    const step: MidiCcStep = {
      target: pedalBridgeRowId(pd) ?? '',
      port,
      type: 'midi.cc',
      id: ulid(),
      channel: pd.channel + 1, // item is 0-based, schema is 1-16
      cc: CC_SNAPSHOT,
      value: snapshotCcValue(props.item.snapshot),
    };

    const results: FireResult[] = await fireAction(step);
    // DEC-124's principle: report what actually happened. Unlike the compound
    // preset fire there is no partial-send case here — one message either
    // lands or it does not.
    const ok = results.length > 0 && results.every((r) => r.kind === 'ok');
    setFault(!ok);
    // "Last commanded" tracker for a DisplayItem bound to this pedal (only on
    // success - a failed send didn't actually change anything on the unit).
    if (ok) {
      recordHelixCommand(pd.id, { kind: 'snapshot', snapshot: props.item.snapshot, at: Date.now() });
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return; // right-click escape hatch
    if (!live()) {
      // Same reasoning as PresetChange: a dead tile currently just fails to
      // respond to a tap. Surface why, loud, at the moment the operator
      // actually reached for it, not just as static face text.
      pushToast('warn', `${faceText()} - ${subText()}`);
      return;
    }
    e.stopPropagation();
    if (!guard.gate('fire')) return; // guarded: first tap only arms
    setFault(false);
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const faceText = () => props.item.name ?? snapshotLabel(props.item.snapshot);

  /* Face flags - same rule and same reason as PresetChange: "Show info" hides
     the routine readout, never a dead state. */
  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel || isFault();
  const isFault = () =>
    !props.item.pedalId || !pedal() || noPort() || !bridgeConnectedReactive(pedalBridgeRowId(pedal())) || fault();

  const subText = () => {
    if (!props.item.pedalId) return 'no device assigned';
    if (!pedal()) return 'device removed';
    if (noPort()) return 'no device port set';
    if (!bridgeConnectedReactive(pedalBridgeRowId(pedal()))) return 'no bridge';
    // Reports in TEXT, not colour — `data-fault` paints nothing in any
    // stylesheet, so a colour-only fault is indistinguishable from success.
    // Same call as PresetChange, and for the same reason.
    if (fault()) return 'send failed';
    if (props.item.snapshot === 'next') return 'next snapshot';
    if (props.item.snapshot === 'prev') return 'prev snapshot';
    return `snapshot ${props.item.snapshot + 1}`;
  };

  /** This tile's snapshot is the last one commanded to its pedal: SENT (DEC-321 rule 1). Next / prev move, so only
   *  an absolute snapshot can match. */
  const sentHere = (): boolean => {
    const c = props.item.pedalId ? helixLastCommanded()[props.item.pedalId] : undefined;
    return !!c && c.kind === 'snapshot' && typeof props.item.snapshot === 'number' && c.snapshot === props.item.snapshot;
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-sent={sentHere() ? 'true' : undefined}
      data-on={timelineLit(props.item.id) ? 'true' : 'false'}
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      onPointerDown={onPointerDown}
      data-disabled={live() ? 'false' : 'true'}
    >
      <PadDecor item={props.item} />
      {/* SENT (DEC-321 rule 1): what only OPButtons knows; the device never reports, so never the flood. */}
      <Show when={sentHere()}><span class="sent-tag" aria-label="sent">SENT</span></Show>
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && (
        <span class="midi-button-label">
          {isDisconnected() && <IconUnlinked />}
          {subText()}
        </span>
      )}
    </button>
  );
}
